import type { ExportFormat } from './download'
import type { PartKind } from './grid'

/**
 * The artefact (deliverable) type — the browser half of backend/domain/artefact-type.ts (AB#197).
 * The web app cannot import backend source, so this is a hand-maintained parity file, exactly like
 * lib/grid.ts and lib/diff.ts. KEEP THE TWO IN SYNC: both are asserted against
 * shared/artefact-types.fixture.json (lib/artefact-type.test.ts here).
 *
 *   prose  (default)  markdown + grid parts   → Word, web page, Markdown, Excel. No PowerPoint.
 *   slides            slides + grid parts     → PowerPoint only (a grid becomes table slides).
 *   grid              grid parts only         → Excel only (AB#263).
 *
 * Chosen at creation and immutable afterwards. Review parts (workflow-owned) are exempt from both
 * rules. Grid parts stay beta-gated on top of this (lib/beta.ts): the type says a grid MAY sit in an
 * artefact, the beta gate says whether this member is offered one — and, while grids are beta,
 * whether they are offered a grid artefact at all.
 */

export type ArtefactType = 'prose' | 'slides' | 'grid'

export const ARTEFACT_TYPES: readonly ArtefactType[] = ['prose', 'slides', 'grid']

/** What an artefact is when nothing says otherwise — every artefact that predates the type. */
export const DEFAULT_ARTEFACT_TYPE: ArtefactType = 'prose'

/** A value read leniently off the wire: anything unknown or absent is prose. */
export function artefactTypeOf(v: unknown): ArtefactType {
  return typeof v === 'string' && (ARTEFACT_TYPES as readonly string[]).includes(v)
    ? (v as ArtefactType)
    : DEFAULT_ARTEFACT_TYPE
}

const PART_KINDS: Record<ArtefactType, readonly PartKind[]> = {
  prose: ['markdown', 'grid'],
  slides: ['slides', 'grid'],
  grid: ['grid'],
}

const EXPORT_FORMATS: Record<ArtefactType, readonly ExportFormat[]> = {
  prose: ['docx', 'html', 'md', 'xlsx'],
  slides: ['pptx'],
  grid: ['xlsx'],
}

/** The part kinds an artefact of this type may hold (review parts aside). */
export function allowedPartKinds(type: ArtefactType): readonly PartKind[] {
  return PART_KINDS[type]
}

/** The formats a part or the whole of an artefact of this type exports to. */
export function allowedExportFormats(type: ArtefactType): readonly ExportFormat[] {
  return EXPORT_FORMATS[type]
}

/**
 * The formats a whole-bid ZIP may be requested in. The chosen format applies to the PROSE
 * deliverables; a slides deliverable always lands in the ZIP as its .pptx deck and a grid one as
 * its .xlsx workbook, so PowerPoint itself is not a bid-scope choice.
 */
export const BID_EXPORT_FORMATS: readonly ExportFormat[] = EXPORT_FORMATS.prose
